/**
 * 登录页（`/login`）。
 *
 * - 只用用户名 + 密码；会话 cookie 由服务端下发（HttpOnly），前端不接触 token。
 * - 登录成功后回跳 `?redirect=` 指定的原页面；`safeRedirect` 把外部/协议相对地址挡掉
 *   （避免开放重定向）。
 * - 失败按 code 展示：`INVALID_CREDENTIALS` 是「用户名或密码错误」，
 *   网络/5xx 才是「服务不可用」——两者不能混成一句话。
 */

import { useState, type FormEvent, type ReactElement } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { ApiClientError } from "../lib/apiClient.js";
import { describeError } from "../hooks/useApiResource.js";
import { safeRedirect } from "../lib/redirect.js";
import { signIn } from "../lib/session.js";

export function LoginPage(): ReactElement {
  const search = useSearch({ from: "/login" });
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await signIn(username.trim(), password);
      const target = safeRedirect(search.redirect);
      const parts = target.split("?");
      const pathname = parts[0] ?? "/";
      const query = new URLSearchParams(parts[1] ?? "");
      const nextSearch: Record<string, string> = {};
      query.forEach((value, key) => {
        nextSearch[key] = value;
      });
      await navigate({ to: pathname as "/", search: nextSearch as never, replace: true });
    } catch (cause: unknown) {
      if (cause instanceof ApiClientError && cause.isInvalidCredentials) {
        setError("用户名或密码错误");
      } else {
        setError(describeError(cause));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex h-screen w-full items-center justify-center bg-canvas px-4">
      <form
        onSubmit={(event) => void submit(event)}
        className="w-full max-w-sm rounded-xl border border-line bg-surface p-6 elev-lg"
        aria-label="登录"
      >
        <div className="flex items-center gap-2">
          <span className="grid h-7 w-7 place-items-center rounded-md bg-accent text-2xs font-bold text-accent-ink">
            AMS
          </span>
          <h1 className="text-lg font-semibold text-ink">登录广告管理系统</h1>
        </div>
        <p className="mt-1 text-meta text-ink-3">
          会话由服务端 HttpOnly cookie 维护；浏览器本地不保存任何令牌。
        </p>

        <label className="mt-5 block text-meta font-medium text-ink-2" htmlFor="login-username">
          用户名
        </label>
        <input
          id="login-username"
          name="username"
          value={username}
          autoComplete="username"
          autoFocus
          required
          onChange={(event) => setUsername(event.target.value)}
          className="mt-1 w-full rounded-control border border-line bg-surface px-2 text-table text-ink outline-none focus-visible:border-accent"
          style={{ height: "var(--control-height)" }}
        />

        <label className="mt-4 block text-meta font-medium text-ink-2" htmlFor="login-password">
          密码
        </label>
        <input
          id="login-password"
          name="password"
          type="password"
          value={password}
          autoComplete="current-password"
          required
          onChange={(event) => setPassword(event.target.value)}
          className="mt-1 w-full rounded-control border border-line bg-surface px-2 text-table text-ink outline-none focus-visible:border-accent"
          style={{ height: "var(--control-height)" }}
        />

        {error === null ? null : (
          <p role="alert" className="mt-4 rounded-control border border-danger/40 bg-danger-subtle px-2 py-1.5 text-meta text-danger-text">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={busy}
          className="mt-5 w-full rounded-control bg-accent px-3 text-table font-medium text-accent-ink transition-colors dur-fast hover:bg-accent-hover disabled:opacity-50"
          style={{ height: "var(--control-height)" }}
        >
          {busy ? "登录中…" : "登录"}
        </button>

        <p className="mt-4 text-2xs text-ink-3">
          未登录访问业务页会被重定向到这里；登录成功后会回到原页面。
        </p>
      </form>
    </div>
  );
}
